import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactHtmlParser from "react-html-parser";
import { FormattedMessage } from "react-intl";

import "./RateCardRates.scss";
import { useGetRateCardDetails } from "api/queries/rate-card/useGetRateCardDetails";
import { replaceLinebreaks, formatNumber } from "helpers/commonHelpers";

const ratesSelector = ({ rates, restrictions }: any = {}) => ({ rates, restrictions });

const RESTRICTION_UNITS: any = {
  weight: "kg",
  "weight/measure": "Tonne",
  cbm: "CBM",
  width_cm: "cm (width)",
  height_cm: "cm (height)",
  length_cm: "cm (length)",
  number_of_cartons: "cartons",
  number_of_pallets: "pallets",
  number_of_containers: "containers",
};

const COST_ITEM_TYPE_LABEL: any = {
  air_freight: "per Air Freight",
  fuel_surcharges: "per Fule Surcharges",
  security_surcharges: "per Security Surcharges",
  additional_cost: "per Shipment",
  additional_cost_per_kg: "per KG",
  additional_cost_per_value: "per Value",
  per_container_cost: "per Container",
  time_sensitive: "per Duration",
  transit: "per Transit",
  import: "per Import",
  export: "per Export",
  km: "per KM",
  mi: "per MI",
  cbm: "per CBM",
  per_weight_measure: "per Weight Measure",
  index_based: "per Index",
};

function RateCardRates() {
  const { id } = useParams();
  const { data } = useGetRateCardDetails(id as string, { refetchOnMount: false, select: ratesSelector });
  const { rates, restrictions } = data;
  const [measurementColLeft, setMeasurementColLeft] = useState(0);
  const [costStructureColLeft, setCostStructureColLeft] = useState(0);
  const costItemColRef = useRef<HTMLTableHeaderCellElement>(null);
  const measurementColRef = useRef<HTMLTableHeaderCellElement>(null);

  useEffect(() => {
    if (costItemColRef?.current?.offsetWidth) {
      setMeasurementColLeft(costItemColRef.current.offsetWidth + 1);
      if (measurementColRef.current?.offsetWidth) {
        setCostStructureColLeft(costItemColRef.current.offsetWidth + measurementColRef.current.offsetWidth + 1);
      }
    }
  }, []);

  const constructRestrictionLabel = (restriction: any) => {
    if (restriction?.container_type) {
      return restriction.container_type;
    }
    if (restriction?.restriction === "max") {
      return `Max ${formatNumber(restriction.max_value)} ${RESTRICTION_UNITS[restriction.restriction_type]}`;
    }
    if (restriction?.restriction === "min") {
      return `Min ${formatNumber(restriction.min_value)} ${RESTRICTION_UNITS[restriction.restriction_type]}`;
    }
    if (restriction?.restriction === "range") {
      return `${formatNumber(restriction.min_value)} - ${restriction.max_value} ${
        RESTRICTION_UNITS[restriction.restriction_type]
      }`;
    }
  };

  return (
    <div className="rate-card-rates">
      <table className="rate-card-table w-100">
        <thead className="rc-table-header">
          <th className="rc-cost-item-col" ref={costItemColRef}>
            <FormattedMessage id="settings.rate-cards.costItemName" defaultMessage="Cost Item Name" />
          </th>
          <th className="rc-measurement-col" ref={measurementColRef} style={{ left: measurementColLeft }}>
            <FormattedMessage id="settings.rate-cards.measurement" defaultMessage="Measurement" />
          </th>
          <th className="rc-cost-structure-col" style={{ left: costStructureColLeft }}>
            <FormattedMessage id="settings.rate-cards.costStructure" defaultMessage="Cost Structure" />
          </th>
          {restrictions &&
            Object.values(restrictions).map((restrictionData: any) => (
              <th className="rc-restriction-col">
                {ReactHtmlParser(
                  replaceLinebreaks(
                    restrictionData.map((restriction: any) => constructRestrictionLabel(restriction)).join("\n"),
                  ),
                )}
              </th>
            ))}
        </thead>
        <tbody>
          {!!rates?.length &&
            rates.map((rate: any) => (
              <>
                <tr>
                  <th className="rc-cost-item-group-header rc-sticky">
                    <div>{rate?.cost_item_group}</div>
                  </th>
                  <th className="rc-cost-item-group-header" colSpan={(Object.values(restrictions)?.length || 0) + 2}>
                    <div />
                  </th>
                </tr>
                {!!rate?.charges?.length &&
                  rate.charges.map((charge: any) => (
                    <>
                      {charge?.cost_structure?.length &&
                        charge.cost_structure.map((cost: any, index: number) => (
                          <tr>
                            <td className="font-weight-bold rc-cost-item-col">{index === 0 ? charge.cost_name : ""}</td>
                            <td className="font-weight-bold rc-measurement-col" style={{ left: measurementColLeft }}>{`${
                              charge.currency
                            } ${COST_ITEM_TYPE_LABEL[charge.cost_item_type]}`}</td>
                            <td className="font-weight-bold rc-cost-structure-col" style={{ left: costStructureColLeft }}>
                              {cost.label}
                            </td>
                            {Object.keys(restrictions).map((restrictionKey: string) => (
                              <td className="rc-restriction-col">
                                {formatNumber(charge.cost_items[restrictionKey][cost.key])}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </>
                  ))}
              </>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default RateCardRates;
